import {authFetch} from "../../authentication/authFetch";

const API_URL = process.env.REACT_APP_API_URL;

function normalizeNote(note) {
    if (!note) return null;

    const createdAt = note.createdAt ?? note.created_at ?? note.createdDate ?? note.created_date ?? null;
    const updatedAt = note.updatedAt ?? note.updated_at ?? note.updatedDate ?? note.updated_date ?? null;
    const endDate = note.endDate ?? note.end_date ?? null;

    return {
        id: note.id,
        userId: note.userId ?? note.user_id ?? null,
        tags: note.tags ?? '',
        createdAt,
        createdDate: createdAt,
        updatedAt,
        updatedDate: updatedAt,
        endDate,
        title: note.title ?? '',
        content: note.content ?? '',
        plainText: note.plainText ?? note.plain_text ?? null,
        taskId: note.taskId ?? note.task_id ?? null,
        projectId: note.projectId ?? note.project_id ?? null,
        parentFolderId: note.parentFolderId ?? note.parent_folder_id ?? null,
        noteSource: note.noteSource ?? note.note_source ?? null,
        sessionId: note.sessionId ?? note.session_id ?? null,
        linkedNotes: note.linkedNotes ?? note.linked_notes ?? null,
        recallCards: note.recallCards ?? note.recall_cards ?? null,
        reviewedAt: note.reviewedAt ?? note.reviewed_at ?? note.lastRevisionDate ?? note.last_revision_date ?? null,
        nextReviewDate: note.nextReviewDate ?? note.next_review_date ?? note.nextRevisionDate ?? note.next_revision_date ?? null,
        reviewStage: note.reviewStage ?? note.review_stage ?? 0,
    };
}

export const createNote = async (authToken, noteData) => {
    if (!authToken) throw new Error('Missing auth token');

    const body = {
        tags: noteData.tags ?? '',
        title: noteData.title ?? '',
        content: noteData.content ?? '',
        parent_folder_id: noteData.parentFolderId ?? null,
    };

    if (noteData.noteSource != null) {
        body.note_source = noteData.noteSource;
    }

    if (noteData.sessionId != null) {
        body.session_id = noteData.sessionId;
    }

    if (noteData.projectId != null) {
        body.project_id = noteData.projectId;
    }

    const response = await authFetch(`${API_URL}/api/note`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        throw new Error('Failed to create note');
    }

    return normalizeNote(await response.json());
};

export const updateNote = async (id, authToken, noteData) => {
    if (!authToken) throw new Error('Missing auth token');

    const body = {
        tags: noteData.tags ?? '',
        title: noteData.title ?? '',
        content: noteData.content ?? '',
        parent_folder_id: noteData.parentFolderId ?? '',
    };

    if (noteData.noteSource != null) {
        body.note_source = noteData.noteSource;
    }

    if (noteData.sessionId != null) {
        body.session_id = noteData.sessionId;
    }

    if (noteData.projectId != null) {
        body.project_id = noteData.projectId;
    }

    const response = await authFetch(`${API_URL}/api/note/${id}`, {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        throw new Error('Failed to update note');
    }

    return normalizeNote(await response.json());
};

export const reviewNote = async (id, authToken) => {
    if (!authToken) throw new Error('Missing auth token');

    const response = await authFetch(`${API_URL}/api/note/${id}/review`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) throw new Error('Failed to review note');
    return normalizeNote(await response.json());
};

export const fetchNoteById = async (id, authToken) => {
    if (!authToken) return null;
    const response = await authFetch(`${API_URL}/api/note/${id}`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to fetch note');
    return normalizeNote(await response.json());
};

export const deleteNote = async (id, authToken) => {
    if (!authToken) return;
    const response = await authFetch(`${API_URL}/api/note/${id}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to delete note');
    return await response.json();
};

export const fetchNotes = async (authToken) => {
    if (!authToken) return [];

    try {
        const response = await authFetch(`${API_URL}/api/notes`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) throw new Error('Failed to fetch notes');
        const payload = await response.json();
        const notes = Array.isArray(payload) ? payload : payload?.items ?? [];

        return notes.map(normalizeNote).filter(Boolean);
    } catch (error) {
        throw error;
    }
};

export const fetchDueNotes = async (authToken) => {
    if (!authToken) return [];

    const response = await authFetch(`${API_URL}/api/notes/due`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to fetch due notes');

    const payload = await response.json();
    const notes = Array.isArray(payload) ? payload : payload?.items ?? [];
    return notes.map(normalizeNote).filter(Boolean);
};

export const fetchProjectNotes = async (authToken, projectId) => {
    if (!authToken || !projectId) return [];

    const response = await authFetch(`${API_URL}/api/project/${projectId}/notes`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) throw new Error('Failed to fetch project notes');

    const payload = await response.json();
    const notes = Array.isArray(payload) ? payload : payload?.items ?? [];

    return notes.map(normalizeNote).filter(Boolean);
};
